import { array, boolean, mixed, object, string } from '@calendly/form/validation'
import { I18n } from '@calendly/i18n'
import { omit } from 'lodash-es'

export const getInitialValues = (eventType) => ({
  confirmationPageType: eventType.confirmation_page_type,
  anotherEventButtonEnabled: eventType.another_event_button_enabled,
  anotherEventButtonLabel: eventType.another_event_button_content,
  customLinks: eventType.custom_links,
  redirectConfigurationUrl: eventType.redirect_configuration?.url || '',
  redirectConfiguration: {
    id: eventType.redirect_configuration?.id,
    passBookingDetails: eventType.redirect_configuration?.pass_booking_details || '',
  },
})

const formatInternalConfirmationPageValues = (values) => {
  if (values.confirmationPageType !== 'internal') return {}

  return {
    another_event_button_enabled: values.anotherEventButtonEnabled,
    another_event_button_content: values.anotherEventButtonLabel,
    custom_links_attributes: values.customLinks.filter(
      (link) => (String(link.id).startsWith('new') ? !link._destroy : true)
    ).map(
      (link) => (String(link.id).startsWith('new') ? omit(link, 'id') : link)
    )
  }
}

const formatExternalConfirmationPageValues = (values) => {
  if (values.confirmationPageType !== 'external') return {}

  return {
    redirect_configuration_attributes: {
      id: values.redirectConfiguration.id,
      url: values.redirectConfigurationUrl,
      pass_booking_details: values.redirectConfiguration.passBookingDetails || false
    }
  }
}

export const formatValuesToServerData = (values) => ({
  confirmation_page_type: values.confirmationPageType,
  ...formatInternalConfirmationPageValues(values),
  ...formatExternalConfirmationPageValues(values),
})

const applyRequired = (schema, message) => (
  message ? schema.required(message) : schema.required()
)

const getCustomLinksValidationSchema = ({ useCrudValidationMessages = false } = {}) => {
  const linkUrlRequiredMessage = useCrudValidationMessages
    ? I18n.t('publisher.event_type_editor_crud.confirmation.validation.link_url_required')
    : undefined
  const linkNameRequiredMessage = useCrudValidationMessages
    ? I18n.t('publisher.event_type_editor_crud.confirmation.validation.link_name_required')
    : undefined
  const redirectUrlRequiredMessage = useCrudValidationMessages
    ? I18n.t('publisher.event_type_editor_crud.confirmation.validation.redirect_url_required')
    : undefined

  return object({
    customLinks: array().when('confirmationPageType', {
      is: 'internal',
      then: (schema) => schema.of(object({
        _destroy: boolean(),
        url: string().when('_destroy', {
          is: true,
          then: (linksSchema) => linksSchema.notRequired(),
          otherwise: (linksSchema) => applyRequired(
            linksSchema.url(I18n.t('activerecord.errors.models.custom_link.attributes.url.url')),
            linkUrlRequiredMessage
          )
        }),
        content: string().when('_destroy', {
          is: true,
          then: (contentSchema) => contentSchema.notRequired(),
          otherwise: (contentSchema) => applyRequired(contentSchema, linkNameRequiredMessage)
        })
      })),
      otherwise: (schema) => schema.notRequired()
    }),
    redirectConfigurationUrl: mixed().when('confirmationPageType', {
      is: 'external',
      then: () => string().when('_destroy', {
        is: true,
        then: (redirectSchema) => redirectSchema.notRequired(),
        otherwise: (redirectSchema) => applyRequired(
          redirectSchema.url(I18n.t('activerecord.errors.models.custom_link.attributes.url.url')),
          redirectUrlRequiredMessage
        )
      })
    })
  })
}

export const getValidationSchema = ({ useCrudValidationMessages = false } = {}) => object({})
  .concat(getCustomLinksValidationSchema({ useCrudValidationMessages }))
